import React, {useContext, useState} from "react";
import {GlobalStateContext} from "../../../global/context/GlobalStateContext";
import {VaprDeviceOverviewContext} from "../context/VaprDeviceOverviewContext";
import {assetTypeToConfigTypeDict} from "../../../util/Util";
import {
    IP_ADDRESS_PATTERN,
    isValidEntry,
    MAC_MASK,
    PATTERNS,
    PROTOCOLS
} from "../../../util/Validations";
import {injectIntl} from "react-intl";
import Modal, {ModalFooter} from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Select, {SelectOption} from "@amzn/meridian/select";
import Alert from "@amzn/meridian/alert";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import {FeatureUtility, FEATURES} from "../../../util/FeatureUtility";
import {getLocalizedMessage} from "../../../util/Localization";
import {setVaprDeviceDetails, updateVaprDeviceDetails} from "../network/VaprDeviceNetworkAPI";

const AddVaprSingleDeviceModal = (props) => {

    const {open, setOpen, selected, setSelected} = props;
    const {showWarning} = useContext(GlobalStateContext);
    const {viewModel, dispatch} = useContext(VaprDeviceOverviewContext);

    const [showAlert, setShowAlert] = useState(false);
    const [showDuplicateDeviceIdAlert, setShowDuplicateDeviceIdAlert] = useState(false);

    // States
    const [mac, setMac] = useState("");
    const [ip, setIp] = useState("");
    const [port, setPort] = useState("");
    const [deviceType, setDeviceType] = useState("");
    const [name, setName] = useState("");
    const [id, setId] = useState("");
    const [detectionFovLength, setDetectionFovLength] = useState("");
    const [detectionFovBreadth, setDetectionFovBreadth] = useState("");
    const [projectionFovLength, setProjectionFovLength] = useState("");
    const [projectionFovBreadth, setProjectionFovBreadth] = useState("");

    // Errors
    const [macErr, setMacErr] = useState(false);
    const [ipErr, setIpErr] = useState(false);
    const [portErr, setPortErr] = useState(false);
    const [deviceTypeErr, setDeviceTypeErr] = useState(false);
    const [nameErr, setNameErr] = useState(false);
    const [idErr, setIdErr] = useState(false);
    const [detectionFovLengthErr, setDetectionFovLengthErr] = useState(false);
    const [detectionFovBreadthErr, setDetectionFovBreadthErr] = useState(false);
    const [projectionFovLengthErr, setProjectionFovLengthErr] = useState(false);
    const [projectionFovBreadthErr, setProjectionFovBreadthErr] = useState(false);

    const initialMonitoringValue = {startTime: "0", endTime: "0", reason: ""};
    const [monitoring, setMonitoring] = useState(JSON.stringify(initialMonitoringValue));
    const {showCustomAlert} = useContext(GlobalStateContext);

    const fovEnabled = FeatureUtility.getFeatureStatus(FEATURES.IS_VAPR_FOV_ENABLED);

    const stateValues = {
        name,
        mac,
        ip,
        port,
        deviceType,
        deviceConfig: {
            type: assetTypeToConfigTypeDict.typeVAPR,
            id
        },
        // FOV fields are gated behind the FOV flag; omitted from the payload while off.
        ...(fovEnabled && {
            detectionFov: JSON.stringify({length: Number(detectionFovLength), breadth: Number(detectionFovBreadth), unit: "mm"}),
            projectionFov: JSON.stringify({length: Number(projectionFovLength), breadth: Number(projectionFovBreadth), unit: "mm"})
        }),
        monitoring
    };

    // Excludes the device's own record (matched by MAC) so editing a device does not flag itself.
    const isDuplicateDeviceId = () => {
        return Object.values(viewModel.vaprDeviceInformation)
            .filter(device => device.mac.toLowerCase() !== mac.toLowerCase())
            .some(device => device.deviceId === id);
    };

    const validateForm = () => {
        return isValidEntry(mac, PATTERNS.MAC) &&
            isValidEntry(ip, IP_ADDRESS_PATTERN) &&
            isValidEntry(port, PATTERNS.PORT) &&
            PROTOCOLS.VAPR.includes(deviceType) &&
            isValidEntry(name, PATTERNS.NAME) &&
            isValidEntry(id, PATTERNS.VAPR_DEVICE_ID) &&
            // FOV validation only applies while the fields are enabled.
            (!fovEnabled || (
                isValidEntry(detectionFovLength, PATTERNS.INTEGER) &&
                isValidEntry(detectionFovBreadth, PATTERNS.INTEGER) &&
                isValidEntry(projectionFovLength, PATTERNS.INTEGER) &&
                isValidEntry(projectionFovBreadth, PATTERNS.INTEGER)
            ));
    };

    const onClickConfirm = () => {
        if (!validateForm()) {
            setMacErr(!isValidEntry(mac, PATTERNS.MAC));
            setIpErr(!isValidEntry(ip, IP_ADDRESS_PATTERN));
            setPortErr(!isValidEntry(port, PATTERNS.PORT));
            setDeviceTypeErr(!PROTOCOLS.VAPR.includes(deviceType));
            setNameErr(!isValidEntry(name, PATTERNS.NAME));
            setIdErr(!isValidEntry(id, PATTERNS.VAPR_DEVICE_ID));
            if (fovEnabled) {
                setDetectionFovLengthErr(!isValidEntry(detectionFovLength, PATTERNS.INTEGER));
                setDetectionFovBreadthErr(!isValidEntry(detectionFovBreadth, PATTERNS.INTEGER));
                setProjectionFovLengthErr(!isValidEntry(projectionFovLength, PATTERNS.INTEGER));
                setProjectionFovBreadthErr(!isValidEntry(projectionFovBreadth, PATTERNS.INTEGER));
            }
            setShowAlert(true);
            setShowDuplicateDeviceIdAlert(false);
            return;
        }
        // Checked only once the form is valid, so a blank or malformed ID gets the format error, not this one.
        if (isDuplicateDeviceId()) {
            setShowDuplicateDeviceIdAlert(true);
            setShowAlert(false);
            return;
        }
        setShowDuplicateDeviceIdAlert(false);
        showWarning(getLocalizedMessage({intl: props.intl, stringId: "device.editWarning"}), () => {
            setShowAlert(false);
            const submitValues = {...stateValues};
            submitValues.deviceConfig = JSON.stringify(submitValues.deviceConfig);
            if (selected) {
                updateVaprDeviceDetails(dispatch, submitValues, showCustomAlert);
            } else {
                setVaprDeviceDetails(dispatch, submitValues, showCustomAlert);
            }
            resetInputState();
            setSelected("");
            setOpen(false);
        });
    };

    const prefill = (value) => {
        if (viewModel.vaprDeviceInformation.hasOwnProperty(value)) {
            setSelected(value);
            const device = viewModel.vaprDeviceInformation[value];
            resetAssetDetailErrors();
            setMac(device.mac);
            setIp(device.ip);
            setPort(device.port);
            setDeviceType(device.deviceType);
            setName(device.name);
            setId(device.deviceId);
            setDetectionFovLength(device.detectionFovLength ? String(device.detectionFovLength) : "");
            setDetectionFovBreadth(device.detectionFovBreadth ? String(device.detectionFovBreadth) : "");
            setProjectionFovLength(device.projectionFovLength ? String(device.projectionFovLength) : "");
            setProjectionFovBreadth(device.projectionFovBreadth ? String(device.projectionFovBreadth) : "");
            const deviceMonitoring = {
                startTime: device.monitoringStartTime,
                endTime: device.monitoringEndTime,
                reason: ""
            };
            setMonitoring(JSON.stringify(deviceMonitoring));
        } else if (selected) {
            setSelected("");
            resetAssetDetails();
        }
    };

    const resetAssetDetails = () => {
        setIp("");
        setPort("");
        setDeviceType("");
        setName("");
        setId("");
        setDetectionFovLength("");
        setDetectionFovBreadth("");
        setProjectionFovLength("");
        setProjectionFovBreadth("");
        setMonitoring(JSON.stringify(initialMonitoringValue));
    };

    const resetAssetDetailErrors = () => {
        setIpErr(false);
        setPortErr(false);
        setDeviceTypeErr(false);
        setNameErr(false);
        setIdErr(false);
        setDetectionFovLengthErr(false);
        setDetectionFovBreadthErr(false);
        setProjectionFovLengthErr(false);
        setProjectionFovBreadthErr(false);
        setShowDuplicateDeviceIdAlert(false);
    };

    const resetInputState = () => {
        setMac("");
        setMacErr(false);
        setMonitoring(JSON.stringify(initialMonitoringValue));
        resetAssetDetails();
        resetAssetDetailErrors();
        setShowAlert(false);
    };

    const onCloseModal = () => {
        setOpen(false);
        setSelected("");
        resetInputState();
    };

    return (
        <Modal
            title={getLocalizedMessage({intl: props.intl, stringId: "device.add_device_button_modal"})}
            open={open}
            onOpen={() => prefill(selected)}
            onClose={onCloseModal}
            scrollContainer="viewport"
            closeLabel="Close"
            bodySpacingInset="medium"
            width="100%">
            <Column spacing="medium" spacingInset="medium">
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.mac"})}
                    </Text>
                    <Input
                        value={mac}
                        onChange={setMac}
                        mask={MAC_MASK}
                        disabled={!!selected}
                        onBlur={() => {
                            setMacErr(!isValidEntry(mac, PATTERNS.MAC));
                            prefill(mac);
                        }}
                        error={macErr}
                        placeholder="e.g. aa:bb:cc:dd:ee:ff"
                    />
                    {macErr &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "error.mac"})}
                        </Text>}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.ip"})}
                    </Text>
                    <Input
                        value={ip}
                        onChange={setIp}
                        onBlur={() => setIpErr(!isValidEntry(ip, IP_ADDRESS_PATTERN))}
                        error={ipErr}
                        placeholder="e.g. 10.34.57.101"
                    />
                    {ipErr &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "error.ip"})}
                        </Text>}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.port"})}
                    </Text>
                    <Input
                        value={port}
                        onChange={setPort}
                        onBlur={() => setPortErr(!isValidEntry(port, PATTERNS.PORT))}
                        error={portErr}
                        placeholder="e.g. 9024"
                    />
                    {portErr &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "error.port"})}
                        </Text>}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.protocol"})}
                    </Text>
                    <Select
                        value={deviceType}
                        onChange={(value) => setDeviceType(value)}
                        onBlur={() => setDeviceTypeErr(!deviceType)}
                        placeholder={getLocalizedMessage({intl: props.intl, stringId: "modal.select_protocol"})}
                        error={deviceTypeErr}
                        width="100%">
                        {PROTOCOLS.VAPR.map(protocol => <SelectOption key={protocol} value={protocol} label={protocol}/>)}
                    </Select>
                    {deviceTypeErr &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "error.protocol"})}
                        </Text>}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.name"})}
                    </Text>
                    <Input
                        value={name}
                        onChange={setName}
                        onBlur={() => setNameErr(!isValidEntry(name, PATTERNS.NAME))}
                        error={nameErr}
                        placeholder="e.g. VAPR-101"
                    />
                    {nameErr &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "error.name"})}
                        </Text>}
                </Column>
                <Column spacing="200">
                    <Text tag="label" type="b200" color="primary">
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.device_id"})}
                    </Text>
                    <Input
                        value={id}
                        onChange={(value) => {
                            setId(value);
                            setShowDuplicateDeviceIdAlert(false);
                        }}
                        onBlur={() => setIdErr(!isValidEntry(id, PATTERNS.VAPR_DEVICE_ID))}
                        error={idErr || showDuplicateDeviceIdAlert}
                        placeholder="e.g. B (1-6 uppercase alphanumeric chars)"
                    />
                    {idErr &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "error.device_id"})}
                        </Text>}
                    {showDuplicateDeviceIdAlert &&
                        <Text type="b100" color="error">
                            {getLocalizedMessage({intl: props.intl, stringId: "alert.duplicate_device_id"})}
                        </Text>}
                </Column>
                <Text type="h100" color="primary">Detection Field of View (mm)</Text>
                <Row widths={["fill", "fill"]}>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary">Length</Text>
                        <Input
                            value={detectionFovLength}
                            onChange={setDetectionFovLength}
                            onBlur={() => setDetectionFovLengthErr(fovEnabled && !isValidEntry(detectionFovLength, PATTERNS.INTEGER))}
                            error={detectionFovLengthErr}
                            disabled={!fovEnabled}
                            placeholder="e.g. 1200"
                        />
                        {detectionFovLengthErr &&
                            <Text type="b100" color="error">
                                {getLocalizedMessage({intl: props.intl, stringId: "error.fov"})}
                            </Text>}
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary">Breadth</Text>
                        <Input
                            value={detectionFovBreadth}
                            onChange={setDetectionFovBreadth}
                            onBlur={() => setDetectionFovBreadthErr(fovEnabled && !isValidEntry(detectionFovBreadth, PATTERNS.INTEGER))}
                            error={detectionFovBreadthErr}
                            disabled={!fovEnabled}
                            placeholder="e.g. 1400"
                        />
                        {detectionFovBreadthErr &&
                            <Text type="b100" color="error">
                                {getLocalizedMessage({intl: props.intl, stringId: "error.fov"})}
                            </Text>}
                    </Column>
                </Row>
                <Text type="h100" color="primary">Projection Field of View (mm)</Text>
                <Row widths={["fill", "fill"]}>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary">Length</Text>
                        <Input
                            value={projectionFovLength}
                            onChange={setProjectionFovLength}
                            onBlur={() => setProjectionFovLengthErr(fovEnabled && !isValidEntry(projectionFovLength, PATTERNS.INTEGER))}
                            error={projectionFovLengthErr}
                            disabled={!fovEnabled}
                            placeholder="e.g. 800"
                        />
                        {projectionFovLengthErr &&
                            <Text type="b100" color="error">
                                {getLocalizedMessage({intl: props.intl, stringId: "error.fov"})}
                            </Text>}
                    </Column>
                    <Column spacing="200">
                        <Text tag="label" type="b200" color="primary">Breadth</Text>
                        <Input
                            value={projectionFovBreadth}
                            onChange={setProjectionFovBreadth}
                            onBlur={() => setProjectionFovBreadthErr(fovEnabled && !isValidEntry(projectionFovBreadth, PATTERNS.INTEGER))}
                            error={projectionFovBreadthErr}
                            disabled={!fovEnabled}
                            placeholder="e.g. 600"
                        />
                        {projectionFovBreadthErr &&
                            <Text type="b100" color="error">
                                {getLocalizedMessage({intl: props.intl, stringId: "error.fov"})}
                            </Text>}
                    </Column>
                </Row>
            </Column>
            <ModalFooter>
                <Column>
                    {showAlert && <Alert type="warning" size="small">
                        {getLocalizedMessage({intl: props.intl, stringId: "alert.missingfields"})}
                    </Alert>}
                </Column>
                <Row alignmentHorizontal="right" widths="fit">
                    <Button type="secondary" size="medium" onClick={onCloseModal}>
                        {getLocalizedMessage({intl: props.intl, stringId: "modal.cancel"})}
                    </Button>
                    <Button type="primary" size="medium" onClick={onClickConfirm}>
                        {selected ? getLocalizedMessage({intl: props.intl, stringId: "modal.confirmEdit"}) :
                            getLocalizedMessage({intl: props.intl, stringId: "modal.confirmAdd"})
                        }
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};

export default injectIntl(AddVaprSingleDeviceModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/vaprdevice/modal/AddVaprSingleDeviceModal.js